import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';

/**
 * Everything a French site has to state about who runs it.
 *
 * The site is published by a private individual, not a business, so the
 * publisher is not named: the LCEN (art. 6, III, 2) lets a non-professional
 * publisher stay anonymous provided the host knows who they are, which it
 * does, as the customer. What the law then asks for is the host, and a way to
 * reach the publisher — the contact form, which lands in the back office.
 */
const HOST = {
  name: 'OVH SAS',
  address: '2 rue Kellermann, 59100 Roubaix, France',
  phone: '1007',
};

const UPDATED_ON = '29 septembre 2026';

function Section({ title, children }) {
  return (
    <Box component="section">
      <Typography variant="h3" component="h2" sx={{ mb: 1.25 }}>
        {title}
      </Typography>
      <Stack spacing={1.25}>{children}</Stack>
    </Box>
  );
}

function Paragraph({ children }) {
  return <Typography variant="body1">{children}</Typography>;
}

function LegalNotice() {
  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%', maxWidth: 760 }}>
        <PageHeader
          title="Mentions légales"
          lede={`Qui édite Vie de Quartier, qui l’héberge, et ce qu’il advient de vos données. Dernière mise à jour : ${UPDATED_ON}.`}
        />

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack spacing={4} divider={<Divider flexItem />}>
            <Section title="Éditeur du site">
              <Paragraph>
                Vie de Quartier est édité par un particulier, à titre non professionnel. Conformément
                à l’article 6, III, 2 de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans
                l’économie numérique, son identité a été communiquée à l’hébergeur mentionné
                ci-dessous.
              </Paragraph>
              <Paragraph>
                Pour toute question, passez par le formulaire{' '}
                <MuiLink component={RouterLink} to="/nous-ecrire">
                  Nous écrire
                </MuiLink>
                .
              </Paragraph>
            </Section>

            <Section title="Hébergement">
              <Paragraph>
                Le site est hébergé par {HOST.name}, {HOST.address} — téléphone : {HOST.phone}.
              </Paragraph>
            </Section>

            <Section title="Propriété intellectuelle">
              <Paragraph>
                La structure du site, ses textes et ses éléments graphiques sont protégés par le
                droit d’auteur. Toute reproduction ou représentation, totale ou partielle, sans
                autorisation écrite de l’éditeur est interdite.
              </Paragraph>
              <Paragraph>
                Les contenus publiés par les habitants et les professionnels — publications, avis,
                fiches, photographies — restent la propriété de leurs auteurs, qui en accordent la
                diffusion sur le site du seul fait de les y publier.
              </Paragraph>
            </Section>

            <Section title="Données personnelles">
              <Paragraph>
                Les données collectées — nom d’utilisateur, adresse email, quartier, et le contenu
                que vous publiez — servent à faire fonctionner le service : ouvrir votre session,
                afficher vos publications, vous prévenir d’une décision de modération ou d’un
                rendez-vous. Elles ne sont ni vendues ni cédées à des tiers.
              </Paragraph>
              <Paragraph>
                Elles sont conservées le temps de la vie du compte. Conformément au règlement (UE)
                2016/679 et à la loi « Informatique et Libertés », vous disposez d’un droit d’accès,
                de rectification, d’effacement, de limitation et d’opposition, ainsi que d’un droit
                à la portabilité de vos données. Vos informations se corrigent depuis la page{' '}
                <MuiLink component={RouterLink} to="/mon-compte">
                  Mon compte
                </MuiLink>
                ; pour tout le reste, dont la fermeture du compte, il suffit d’en faire la demande
                par le formulaire{' '}
                <MuiLink component={RouterLink} to="/nous-ecrire">
                  Nous écrire
                </MuiLink>
                . Vous pouvez enfin introduire une réclamation auprès de la CNIL.
              </Paragraph>
            </Section>

            <Section title="Cookies">
              <Paragraph>
                Le site dépose deux cookies, tous deux strictement nécessaires à son fonctionnement.
                Le premier est le cookie de session, qui vous garde connecté d’une page à l’autre.
                Le second, « signed_in », indique seulement à la page qu’une session est ouverte,
                pour qu’elle ne la cherche pas quand il n’y en a pas ; il ne contient aucune
                information sur vous.
              </Paragraph>
              <Paragraph>
                Ils expirent avec votre session et ne servent à aucune mesure d’audience ni à aucune
                publicité ; à ce titre, ils ne requièrent pas votre consentement préalable.
              </Paragraph>
              <Paragraph>
                La fréquentation du site est mesurée sans cookie, par un outil hébergé sur le même
                serveur que lui : les pages consultées sont comptées de façon agrégée, votre adresse
                IP n’est pas conservée, et rien n’est transmis à un tiers.
              </Paragraph>
            </Section>

            <Section title="Responsabilité">
              <Paragraph>
                L’éditeur s’efforce de maintenir le site accessible et les informations à jour, sans
                pouvoir le garantir : les fiches de l’annuaire, les horaires et les disponibilités
                sont renseignés par les professionnels eux-mêmes.
              </Paragraph>
              <Paragraph>
                Les contenus publiés par les utilisateurs n’engagent que leurs auteurs. Un contenu
                qui vous paraît contraire aux règles peut être signalé depuis le site ; il est alors
                examiné par la modération.
              </Paragraph>
            </Section>

            <Section title="Droit applicable">
              <Paragraph>
                Les présentes mentions sont soumises au droit français. En cas de litige, et à défaut
                de résolution amiable, les tribunaux français sont seuls compétents.
              </Paragraph>
            </Section>
          </Stack>
        </Paper>
      </Box>
    </PageLayout>
  );
}

export default LegalNotice;
